/**
 * Data hook for the unified support inbox: the ticket list and the sidebar
 * view/channel counts (Task 5 renders both from this).
 *
 * Two independent queries, deliberately:
 *   - `counts` reads only the columns `matchesView` needs, over every
 *     ticket — never the fetched list page. The list will paginate; a count
 *     derived from that page would under-report the moment it does.
 *   - `list` selects full rows with the same booking/property/room embeds
 *     the old Kanban console's query used, then hydrates reporter/assignee
 *     names from `public.profiles` in a second query: `issues.*` foreign
 *     keys to `auth.users`, which PostgREST cannot embed across. Embedding
 *     `profiles` directly instead of hydrating separately fails silently —
 *     an empty array, no error — which has bitten this repo before.
 *
 * Unread counts mirror the old Kanban console's `get_issue_unread_counts` RPC
 * fetch (same RPC, same `issue_comments` fallback when the RPC throws) so
 * the list's unread badge shows real numbers rather than the permanently-
 * inert placeholder the first pass shipped.
 */
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  SUPPORT_CHANNELS,
  SUPPORT_VIEWS,
  envelopeText,
  inboundSenderEmail,
  inboundSenderPhone,
  matchesView,
  type InboxTicket,
  type InboxTicketRow,
  type SupportChannel,
  type SupportView,
} from "@/lib/support-views";

/** Only the fields the view predicates need — this powers the sidebar counts
 *  and stays cheap as the ticket table grows. Counting a fetched page would
 *  under-report the moment the list paginates. */
const COUNT_COLUMNS =
  "id,status,channel,priority,assigned_to,archived_at,reported_by";

/** Raw `issues` row shape the list query selects, before name hydration. */
type RawIssueRow = InboxTicket & {
  title: string;
  user_id: string | null;
  created_at?: string | null;
  description?: string | null;
  last_message_at?: string | null;
  support_read_at?: string | null;
  /** `issues.metadata` — carries `from_email` on email-sourced tickets. */
  metadata?: unknown;
  bookings?: unknown;
  properties?: { title?: string | null } | null;
  rooms?: { title?: string | null } | null;
};

type ProfileBrief = { id: string; name: string | null; email: string | null };

// Same localStorage key convention IssueChatThread.tsx, Dashboard.tsx and
// the old Kanban console already read/write (`support:lastRead:<scope>:<user>:<issue>`)
// — the fallback below only produces correct counts if it agrees with what
// every other reader/writer already uses. The old console's own copy of this
// wasn't exported, so this is a duplicate by necessity, not by choice (its
// sibling useSupportUnreadCount.ts duplicates it for the same reason).
function getLocalLastRead(
  scope: "tenant" | "admin",
  userId: string,
  issueId: string
): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(
      `support:lastRead:${scope}:${userId}:${issueId}`
    );
  } catch {
    return null;
  }
}

/**
 * Real per-ticket unread counts — mirrors the old Kanban console's `issue-unread`
 * query verbatim (RPC first, `issue_comments` scan against localStorage
 * lastRead stamps if the RPC throws). Never lets a failure here fail the
 * ticket list: both the RPC and the fallback are caught, defaulting to an
 * empty map (every ticket reads as 0 unread) rather than an error state.
 */
async function fetchUnreadCounts(
  issueIds: string[],
  currentUserId: string | null
): Promise<Map<string, number>> {
  if (!currentUserId || !issueIds.length) return new Map();
  try {
    type UnreadRpcRow = { issue_id: string; unread_count?: unknown };
    type RpcUnreadClient = {
      rpc: (
        fn: string,
        args: { issue_ids: string[]; include_internal: boolean }
      ) => Promise<{ data: UnreadRpcRow[] | null; error: Error | null }>;
    };
    const { data, error } = await (supabase as unknown as RpcUnreadClient).rpc(
      "get_issue_unread_counts",
      {
        issue_ids: issueIds,
        include_internal: true,
      }
    );
    if (error) throw error;
    const m = new Map<string, number>();
    for (const row of (data || []) as Array<{
      issue_id: string;
      unread_count: number;
    }>) {
      m.set(row.issue_id, Number(row.unread_count || 0));
    }
    return m;
  } catch (e) {
    console.warn("Unread RPC failed (support inbox), using fallback:", e);
    try {
      const { data: rows, error: rowsErr } = await supabase
        .from("issue_comments")
        .select("issue_id,user_id,created_at,is_internal")
        .in("issue_id", issueIds)
        .order("created_at", { ascending: false });
      if (rowsErr) throw rowsErr;
      const m = new Map<string, number>();
      for (const id of issueIds) m.set(id, 0);
      for (const r of (rows || []) as Array<{
        issue_id: string;
        user_id: string;
        created_at: string;
        is_internal: boolean | null;
      }>) {
        if (!r?.issue_id) continue;
        if (r.user_id === currentUserId) continue;
        const last = getLocalLastRead("admin", currentUserId, r.issue_id);
        const lastMs = last ? Date.parse(last) : 0;
        const msgMs = Date.parse(r.created_at);
        if (msgMs > lastMs) m.set(r.issue_id, (m.get(r.issue_id) || 0) + 1);
      }
      return m;
    } catch (e2) {
      console.warn("Unread fallback also failed (support inbox):", e2);
      return new Map();
    }
  }
}

/**
 * Ids of the tickets whose conversation contains `q` — one server-side ILIKE
 * per list fetch, never per row, and never issued while the search box is
 * empty (the caller gates on `q`), so no message body is ever downloaded just
 * to grep it locally.
 *
 * Scope is the *conversation*, i.e. what the Conversation tab shows:
 *   - soft-deleted messages render as tombstones (IssueChatThread), so text
 *     nobody can read must not be what surfaces a ticket;
 *   - internal notes are included: staff use the inbox search to find their
 *     own operational context as well as customer messages.
 *
 * `%`, `_` and `\` are escaped so the term is a literal substring, the same
 * meaning as the client-side `includes(q)` on title/description (an unescaped
 * trailing `\` would also error the whole query in Postgres).
 *
 * ponytail: PostgREST's max-rows (1000 on Supabase) caps the hit rows — one
 * per matching message, not per ticket. Fine at ~40 tickets; a DISTINCT RPC
 * once a common term matches thousands of messages.
 */
export async function fetchCommentMatchIssueIds(
  q: string
): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("issue_comments")
    .select("issue_id")
    .ilike("comment", `%${q.replace(/[\\%_]/g, "\\$&")}%`)
    .is("deleted_at", null);
  if (error) throw error;
  return new Set((data ?? []).map((r) => r.issue_id));
}

/**
 * Keeps the inbox live.
 *
 * Without this the admin only saw a tenant's reply after a manual refresh or a
 * window-focus refetch — while the *tenant* side (SupportTickets.tsx,
 * SettingsSupportTickets.tsx, useSupportUnreadCount.ts) has subscribed to
 * postgres_changes all along. The staff surface being the stale one is exactly
 * backwards for a shared inbox.
 *
 * Invalidate-on-event rather than patching the cache by hand: the list is a
 * filtered, unread-count-joined projection, so re-running the query is both
 * simpler and correct, and React Query coalesces bursts.
 */
function useSupportInboxRealtime(currentUserId: string | null | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ["support-inbox"] });
      queryClient.invalidateQueries({ queryKey: ["support-ticket-detail"] });
      // The open thread renders from IssueChatThread's own keys, which are
      // prefixed per mount site; the inbox always mounts it as
      // "ticket-detail".
      queryClient.invalidateQueries({ queryKey: ["ticket-detail"] });
    };

    const channel = supabase
      .channel("support-inbox-live")
      // A tenant's emailed reply lands as an issue_comments INSERT via the
      // inbound webhook — this is the event that was missing.
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "issue_comments" },
        invalidate
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "issues" },
        invalidate
      )
      // Status, assignee and archived_at all move through UPDATE, and each
      // changes which view a ticket belongs to.
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "issues" },
        invalidate
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient, currentUserId]);
}

export function useSupportInbox(opts: {
  view: SupportView;
  channel: SupportChannel | null;
  search: string;
  currentUserId: string | null;
  includeCompleted?: boolean;
}) {
  const { t } = useLanguage();

  const counts = useQuery({
    // currentUserId is part of the key, not an `enabled` gate: `mine` is the
    // only view that depends on it, and the other views/counts must not be
    // blocked while auth is still resolving. Without it here, the query
    // computed under a null id (auth not yet resolved) never refetches once
    // a real id lands — react-query sees an unchanged key and keeps serving
    // the stale "mine never matches" result.
    queryKey: ["support-inbox", "counts", opts.currentUserId],
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase
        .from("issues")
        .select(COUNT_COLUMNS);
      if (error) throw error;
      const rows = (data ?? []) as InboxTicket[];

      const out: Record<string, number> = {};
      for (const v of SUPPORT_VIEWS) {
        out[v.key] = rows.filter((r) =>
          matchesView(r, v.key, opts.currentUserId)
        ).length;
      }
      out.archived = rows.filter((r) =>
        matchesView(r, "archived", opts.currentUserId)
      ).length;
      for (const c of SUPPORT_CHANNELS) {
        out[c.key] = rows.filter(
          (r) =>
            r.channel === c.key &&
            matchesView(r, "all-open", opts.currentUserId)
        ).length;
      }
      return out;
    },
    staleTime: 30_000,
  });

  const list = useQuery({
    // Same reasoning as the counts key above: `mine` reads currentUserId.
    queryKey: [
      "support-inbox",
      "list",
      opts.view,
      opts.channel,
      opts.search,
      opts.currentUserId,
      opts.includeCompleted ?? false,
    ],
    queryFn: async (): Promise<InboxTicketRow[]> => {
      const q = opts.search.trim().toLowerCase();
      // The comment lookup is independent of the issues fetch — run them
      // together so a search costs one round trip of latency, not two. An
      // empty box skips it entirely.
      const [{ data, error }, commentHits] = await Promise.all([
        supabase
          .from("issues")
          .select(
            "*, bookings:booking_id (id), properties:property_id (id, title), rooms:room_id (id, title)"
          )
          // Latest exchange, not first — a thread answered a minute ago
          // belongs above a quiet ticket opened yesterday. `created_at` put
          // the busiest conversations hardest to find (V2.3 feedback #3).
          //
          // `last_message_at` is a stored column kept by a trigger on
          // issue_comments (20260930100000). Deliberately NOT updated_at:
          // inserting a comment does not touch the issue row, so updated_at
          // tracks staff admin — status, assignee, archive — not the
          // conversation. It is NOT NULL and indexed, so this sort is total
          // and does not scan.
          .order("last_message_at", { ascending: false }),
        q ? fetchCommentMatchIssueIds(q) : new Set<string>(),
      ]);
      if (error) throw error;
      const rows = (data ?? []) as RawIssueRow[];

      // Hydrate names separately: issues.* references auth.users, not
      // public.profiles, so PostgREST cannot embed across that FK (see file
      // header — getting this wrong yields an empty array, not an error).
      const ids = Array.from(
        new Set(
          rows
            .flatMap((r) => [r.user_id, r.reported_by, r.assigned_to])
            .filter(Boolean)
            .map(String)
        )
      );
      const byId = new Map<string, ProfileBrief>();
      if (ids.length) {
        const { data: profs, error: profErr } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", ids);
        if (profErr) throw profErr;
        for (const p of profs ?? []) byId.set(String(p.id), p);
      }

      const latestByIssue = new Map<string, { text: string; at: string }>();
      const teamUnreadByIssue = new Map<string, number>();
      if (rows.length) {
        const { data: recent, error: recentError } = await supabase
          .from("issue_comments")
          .select("issue_id,comment,created_at,is_system,sender_scope")
          .in("issue_id", rows.map((row) => row.id))
          .is("deleted_at", null)
          .eq("is_system", false)
          .order("created_at", { ascending: false });
        if (recentError) throw recentError;
        for (const item of recent ?? []) {
          if (!latestByIssue.has(item.issue_id)) {
            latestByIssue.set(item.issue_id, {
              text: envelopeText(item.comment),
              at: item.created_at,
            });
          }
          const issue = rows.find((row) => row.id === item.issue_id);
          const readAt = Date.parse(issue?.support_read_at ?? "");
          if (item.sender_scope === "tenant" && Date.parse(item.created_at) > (Number.isFinite(readAt) ? readAt : 0)) {
            teamUnreadByIssue.set(item.issue_id, (teamUnreadByIssue.get(item.issue_id) ?? 0) + 1);
          }
        }
      }

      const hydrated = rows.map((r) => ({
        ...r,
        latestMessage:
          latestByIssue.get(r.id)?.text ?? envelopeText(r.description),
        latestMessageAt:
          latestByIssue.get(r.id)?.at ?? r.last_message_at ?? r.created_at,
        team_unread_count: teamUnreadByIssue.get(r.id) ?? 0,
        tenantName:
          byId.get(String(r.reported_by))?.name ??
          byId.get(String(r.reported_by))?.email ??
          // An UNOWNED ticket — the sender matched no user, so reported_by is
          // NULL (20260914100000_issues_unowned_tickets.sql) and there is no
          // profile to name it after. The address/number it came from is the
          // honest label; "Unknown" would hide exactly the tickets Neel asked
          // to see. Email first (the longer-lived channel), then the WhatsApp
          // number, same order TicketDetail.tsx's header uses.
          inboundSenderEmail(r.metadata) ??
          inboundSenderPhone(r.metadata) ??
          t("adminIssues.unknown", "Unknown"),
        // Same fallback chain the old Kanban console's adminDisplayName used
        // (name, then email, then a stand-in) — never left as a raw id.
        assignedName: r.assigned_to
          ? (byId.get(String(r.assigned_to))?.name ??
            byId.get(String(r.assigned_to))?.email ??
            t("support.list.unknownAssignee", "Unknown admin"))
          : null,
      }));
      const filtered = hydrated
        .filter(
          (r) =>
            opts.includeCompleted ||
            matchesView(r, opts.view, opts.currentUserId)
        )
        .filter((r) => !opts.channel || r.channel === opts.channel)
        // `search` is part of the hook's contract (queryKey above already
        // varies on it, and nothing else feeds this match). Subject, reporter
        // name, and the message text: `description` is the first message on
        // an email-sourced ticket, `commentHits` the rest of the thread.
        .filter(
          (r) =>
            !q ||
            r.title.toLowerCase().includes(q) ||
            r.tenantName.toLowerCase().includes(q) ||
            r.properties?.title?.toLowerCase().includes(q) ||
            r.rooms?.title?.toLowerCase().includes(q) ||
            inboundSenderEmail(r.metadata)?.toLowerCase().includes(q) ||
            inboundSenderPhone(r.metadata)?.includes(q) ||
            envelopeText(r.description).toLowerCase().includes(q) ||
            commentHits.has(r.id)
        );

      // Real unread counts for exactly the rows being returned (not the
      // full unfiltered table — nothing outside this page can show a badge
      // anyway). A fetch failure here degrades every row to 0 unread; see
      // fetchUnreadCounts's own catch blocks.
      const unreadById = await fetchUnreadCounts(
        filtered.map((r) => r.id),
        opts.currentUserId
      );
      return filtered.map((r) => ({
        ...r,
        unread_count: unreadById.get(r.id) ?? 0,
      }));
    },
  });

  useSupportInboxRealtime(opts.currentUserId);

  return {
    tickets: list.data ?? [],
    counts: counts.data ?? {},
    isLoading: list.isLoading,
    // Surfaced separately from `tickets` so a failed query renders an error
    // state instead of looking like an empty inbox.
    error: list.error ?? counts.error,
  };
}
